import { bbox } from "@turf/turf";
import { Basemap } from "components/basemap/basemap";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as QueryKeys from "data";
import { fetchAuthJWT, fetchMapsLayerJson } from "data/queries-common";
import { requestTransformer } from "pages/geoscape-data/explorer/explorer-page";
import { TileJSON } from "pages/geoscape-data/explorer/layers/models";
import { ZoomControl } from "pages/geoscape-data/explorer/zoom-control";
import { useEffect, useRef, useState } from "react";
import { Layer, LayerProps, LineLayerSpecification, MapRef, Source } from "react-map-gl/maplibre";
import { useQuery } from "@tanstack/react-query";
import { PredictiveSuburbManager } from "./predictive-suburb-manager";

export const VisualiseMap = (
  predictiveSuburbManager: PredictiveSuburbManager,
) => {
  const mapRef = useRef<MapRef>();
  const initialZoom: number = 17;
  const [dynamicZoom, setDynamicZoom] = useState(initialZoom);
  const [stateLayerJson, setStateLayerJson] = useState<TileJSON | null>(null);

  const localityLayer: LayerProps = {
    id: "data",
    type: "fill",
    paint: {
      "fill-color": "#00ffff",
      "fill-opacity": 0.2,
    },
  };

  const stateLayer: LineLayerSpecification = {
    id: "state",
    type: "line",
    source: "state",
    "source-layer": "state",
    paint: {
      "line-color": "#0f7ae4",
      "line-width": 1,
    },
  };

  const [minLng, minLat, maxLng, maxLat] = predictiveSuburbManager.locality
    ?.features[0]?.geometry
    ? bbox(predictiveSuburbManager.locality?.features[0].geometry)
    : [96, -45, 168, -8]; // bounding box of Australia

  mapRef.current?.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    { padding: 10, duration: 1000 },
  );

  const { enqueueQueryFailed } = useCustomSnackbars();

  const jwt = useQuery({
    queryKey: [QueryKeys.jwt],
    queryFn: () => fetchAuthJWT(),
  });

  useEffect(() => {
    if (jwt.isError) {
      enqueueQueryFailed(jwt.error.toString());
    }
  }, [jwt.isError]);

  const addAuthToTileUrl = (tileUrl: string[]) => {
    return tileUrl.map((x) => `${x}?key=${jwt.data}`);
  };

  const stateLayerJsonQuery = useQuery({
    queryKey: [QueryKeys.mapsLayerJson, "state"],
    queryFn: () => fetchMapsLayerJson(jwt.isSuccess ? jwt.data : "", "state"),
    enabled: !!jwt.data,
    retry: true,
  });

  useEffect(() => {
    if (stateLayerJsonQuery.isSuccess && stateLayerJsonQuery.data) {
      setStateLayerJson(stateLayerJsonQuery.data);
    }
  }, [stateLayerJsonQuery.isSuccess]);

  const resetCompass = () => {
    if (mapRef.current === undefined) return;
    mapRef.current.easeTo({
      bearing: 0,
      pitch: 0,
    });
  };

  const handleZoom = (zoom: number): void => {
    setDynamicZoom(zoom);
    mapRef.current?.flyTo({ zoom: zoom, duration: 500 });
  };

  return (
    <div className="relative w-full flex-1 min-h-87.5 rounded-lg overflow-hidden border">
      {jwt.isSuccess && (
        <Basemap
          // @ts-ignore
          mapRef={mapRef}
          zoom={initialZoom}
          attributionControl={false}
          style={{ width: "100%", height: "100%" }}
          onZoom={() => {}}
          transformRequest={requestTransformer}
          bbox={[minLng, minLat, maxLng, maxLat]}
          bboxPadding={0}
        >
          {predictiveSuburbManager.locality?.features[0]?.geometry && (
            <Source
              type="geojson"
              data={
                predictiveSuburbManager.locality as GeoJSON.FeatureCollection
              }
            >
              <Layer {...localityLayer} />
            </Source>
          )}
          {stateLayerJson && (
            <Source
              key="state"
              id="state"
              type="vector"
              maxzoom={16}
              tiles={addAuthToTileUrl(stateLayerJson.tiles)}
            >
              <Layer {...stateLayer} />
            </Source>
          )}
          <div className="absolute right-4 top-4 flex flex-col gap-2">
            <ZoomControl
              resetCompass={resetCompass}
              dynamicZoom={dynamicZoom}
              onZoom={handleZoom}
            />
          </div>
        </Basemap>
      )}
    </div>
  );
};
